import { ApiPublicClient, createTokenConfig } from 'api/ApiClient';
import axios from 'axios';
import {
    PortalAddonEntry,
    PortalAddonsError,
    PortalAddonsErrorKind,
    PortalBooking
} from 'types/PortalBooking';

import { convertToPortalBooking } from './PortalBookingConverter';

const bookingUrl = (bookingId: number) => `pet_parent_portal/bookings/${bookingId}`;

export const fetchPortalBooking = async (
    bookingId: number,
    marketplaceId: number
): Promise<PortalBooking> => {
    const { data } = await ApiPublicClient.get(bookingUrl(bookingId), {
        ...createTokenConfig(),
        params: { marketplace_id: marketplaceId }
    });

    return convertToPortalBooking(data.booking ?? data);
};

const toAddonsError = (error: unknown): PortalAddonsError => {
    if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;
        const message = data?.message ?? 'Something went wrong';

        if (status === 403) {
            return { kind: PortalAddonsErrorKind.NotAvailable, message };
        }

        if (data?.code === 'price_changed') {
            return {
                kind: PortalAddonsErrorKind.PriceChanged,
                message,
                currentTotal: data.current_total
            };
        }

        if (data?.code === 'addons_not_available') {
            return {
                kind: PortalAddonsErrorKind.AddonsUnavailable,
                message,
                positions: Array.isArray(data.addons) ? data.addons : []
            };
        }

        if (data?.code === 'booking_not_eligible') {
            return { kind: PortalAddonsErrorKind.NotAvailable, message };
        }

        if (status >= 400 && status < 500) {
            return { kind: PortalAddonsErrorKind.Rejected, message };
        }

        return { kind: PortalAddonsErrorKind.Unknown, message };
    }

    return { kind: PortalAddonsErrorKind.Unknown, message: 'Something went wrong' };
};

/**
 * Adds the entries to the booking in one all-or-nothing request. `expectedTotal` is the sum of
 * price x quantity before taxes: the backend answers `price_changed` when it no longer matches.
 * Resolves with the refreshed booking; rejects with a PortalAddonsError.
 */
export const addPortalBookingAddons = async (
    bookingId: number,
    marketplaceId: number,
    entries: Array<PortalAddonEntry>,
    expectedTotal: number
): Promise<{ addedTotal: number; booking: PortalBooking }> => {
    try {
        const { data } = await ApiPublicClient.post(
            `${bookingUrl(bookingId)}/addons`,
            {
                expected_total: expectedTotal,
                addons: entries.map(entry => ({
                    uuid: entry.uuid,
                    pet_id: entry.petId,
                    addon_service_id: entry.addonServiceId,
                    date: entry.date,
                    quantity: entry.quantity
                }))
            },
            { ...createTokenConfig(), params: { marketplace_id: marketplaceId } }
        );

        return { addedTotal: data.added_total, booking: convertToPortalBooking(data.booking) };
    } catch (error) {
        throw toAddonsError(error);
    }
};
